import Signal from "badmfck-signal";
import { useEffect, useState } from "react";
import { AppRole, Permission } from "src/permissions";

export interface INav {
	path: string;
	id: string;
	label: string;
	page: JSX.Element;
	sub?: INav[];
	icon?: JSX.Element;
	roles?: AppRole[];
	permission?: Permission;
	exclude?: boolean;
}

export interface IBreadcrumbs {
	path: string;
	node: INav;
}

const S_NAV_CHANGED = new Signal<INav>();

export const Inner = () => {
	const nav = S_NAV_CHANGED.use([]);
	if (nav && nav.page) return nav.page;
	return <></>;
};

class Router {
	currentPath: string;
	currentParams: Record<string, string> = {};
	currentNav: INav | null = null;
	breadcrumbs: IBreadcrumbs[] = [];
	#sitemap: INav[];
	static #systemPatched = false;
	static instance: Router | null = null;

	constructor(sitemap: INav[]) {
		this.#sitemap = sitemap;
		this.currentPath = window.location.pathname;
		if (Router.instance) return;
		Router.instance = this;
		this.#patchHistoryMethods();
		window.addEventListener("popstate", this.#onPopState);
		window.addEventListener("locationchange", this.#onLocationChange);
		S_NAV_CHANGED.onSubscribe = cb => this.currentNav && cb!(this.currentNav);
		this.#processPath();
	}

	navigate(id: string, params?: { [key: string]: string }) {
		let path = this.#getPath(id, params);

		if (!path.startsWith("/")) path = "/" + path;

		if (typeof path === "string") {
			if (window.location.pathname !== path) {
				// pushState is patched (#patchHistoryMethods) to synchronously dispatch a
				// "locationchange" event, which the window listener turns into #onLocationChange.
				// Calling #onLocationChange() here as well would process the path — and fire
				// S_NAV_CHANGED — twice per navigation, doubling every subscriber's render.
				history.pushState({}, "", path);
			}
		}
	}

	#getPath(id: string, params?: { [key: string]: string }): string {
		if (!this.#sitemap || this.#sitemap.length === 0) {
			console.warn("No sitemap");
			return "no-sitemap/" + id;
		}

		let node = this.#findNode(this.#sitemap, id);

		if (!node) {
			console.warn(id + " not found in sitemap");
			return "not-found/" + id;
		}

		const path: string[] = [];
		while (node) {
			// generate path
			path.push(node.path);
			node = (node as any).parent;
		}

		let addr = path.reverse().join("/");
		if (params) for (const i in params) addr = addr.replaceAll(":" + i, params[i]);

		return addr;
	}

	#findNode(nodes: INav[], id: string, parent?: INav): INav | null {
		let result = null;
		for (const i of nodes) {
			if (i.id === id) {
				result = i;
				break;
			}
			if (i.sub) {
				const r = this.#findNode(i.sub, id, i);
				if (r) {
					result = r;
					break;
				}
			}
		}
		if (parent && result && !(result as any).parent) {
			(result as any).parent = parent;
		}
		return result;
	}

	#patchHistoryMethods() {
		if (Router.#systemPatched) return;
		Router.#systemPatched = true;
		const wrap = (type: "pushState" | "replaceState") => {
			const original = history[type];
			history[type] = function (...args) {
				const result = original.apply(this, args);
				window.dispatchEvent(new Event("locationchange"));
				return result;
			};
		};
		wrap("pushState");
		wrap("replaceState");
	}

	#onPopState = (_: PopStateEvent) => {
		this.#onLocationChange();
	};

	#onLocationChange = () => {
		this.currentPath = window.location.pathname;
		this.#processPath();
	};

	#processPath() {
		if (!this.currentPath || !this.currentPath.length) {
			return;
		}

		const params: Record<string, string> = {};
		let path = this.currentPath.split("/").filter((val: string) => val !== "" && val !== null);
		let nodes = this.#sitemap;
		let found: INav | null = null;
		this.breadcrumbs = [];

		if (path.length === 0) {
			path = ["/"];
		}

		for (let i = 0; i < path.length; i++) {
			const p = path[i];

			// iterate over nodes
			for (const j of nodes) {
				// simple node
				if (j.path === p) {
					this.breadcrumbs.push({
						path: p,
						node: j,
					});
					found = j;
					if (j.sub) nodes = j.sub;
					break;
				}

				if (j.path.startsWith(":")) {
					this.breadcrumbs.push({
						path: p,
						node: j,
					});
					params[j.path.substring(1)] = p;
					found = j;
					if (j.sub) nodes = j.sub;
					break;
				}

				if (j.path.indexOf("/") !== -1) {
					// split complex node
					const subnodes = j.path.split("/");
					if (p === subnodes[0]) {
						i++;
						params[subnodes[1].substring(1)] = path[i];
						this.breadcrumbs.push({
							path: p,
							node: j,
						});
						this.breadcrumbs.push({
							path: path[i],
							node: j,
						});
						found = j;
						if (j.sub) nodes = j.sub;
						break;
					}
				}
			}
		}

		if (found) {
			this.currentParams = params;
			this.currentNav = found;
			queueMicrotask(() => {
				S_NAV_CHANGED.invoke(found);
			});
		}
	}

	destroy() {
		this.#sitemap = [];
		window.removeEventListener("popstate", this.#onPopState);
		window.removeEventListener("locationchange", this.#onLocationChange);
		Router.instance = null;
		this.currentPath = "";
		this.currentParams = {};
		this.currentNav = null;
		this.breadcrumbs = [];
	}
}

export const useRouter = () => {
	const [router, setRouter] = useState<Router>(Router.instance!);
	useEffect(() => {
		const handler = S_NAV_CHANGED.subscribe(_ => {
			setRouter(Router.instance!);
		});
		return () => {
			S_NAV_CHANGED.unsubscribe(handler);
		};
	}, []);
	return router;
};

export const useBreadcrumbs = () => {
	const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbs[]>([]);
	useEffect(() => {
		const handler = S_NAV_CHANGED.subscribe(_ => {
			setBreadcrumbs([...(Router.instance?.breadcrumbs ?? [])]);
		});
		return () => {
			S_NAV_CHANGED.unsubscribe(handler);
		};
	}, []);
	return breadcrumbs;
};

export const useCurrentPath = () => {
	const [currentPath, setCurrentPath] = useState<{
		currentPath: string;
		currentParams: Record<string, string>;
		currentNav: INav;
	} | null>(null);
	useEffect(() => {
		const handler = S_NAV_CHANGED.subscribe(_ => {
			setCurrentPath({
				currentNav: _,
				currentPath: Router.instance?.currentPath ?? "",
				currentParams: Router.instance?.currentParams ?? {},
			});
		});
		return () => {
			S_NAV_CHANGED.unsubscribe(handler);
		};
	}, []);
	return currentPath;
};

export const useCreateRouter = (sitemap: INav[], view: React.ReactElement) => {
	useEffect(() => {
		//setTimeout(() => {}, 1000);
		new Router(sitemap);
		return () => {
			Router.instance?.destroy();
		};
	}, [sitemap]);
	return view;
};
